import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ShoppingCart, Search, Plus, Minus, Trash2, CreditCard, Banknote, 
  CheckCircle, X, Store, ChevronRight, Sparkles, Package, User,
  StickyNote, ArrowLeft, ShoppingBag
} from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';

const AdminPOS = () => {
  const { fetchWithAuth } = useAdminAuth();
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [notes, setNotes] = useState('');
  const [activeGenderFilter, setActiveGenderFilter] = useState('all');
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);

  const loadLocations = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/admin/pos/locations');
      if (res.ok) {
        const data = await res.json();
        const activeLocations = (data.locations || []).filter(l => l.is_active);
        setLocations(activeLocations);
        if (activeLocations.length === 1) {
          setSelectedLocation(activeLocations[0]);
        }
      }
    } catch (error) {
      toast.error('Error al cargar puntos de venta');
    }
  }, [fetchWithAuth]);

  const loadAllProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const res = await fetchWithAuth('/api/admin/pos/search-product?q=');
      if (res.ok) {
        const data = await res.json();
        setAllProducts(data.products || []);
      }
    } catch (error) {
      console.error('Load products error:', error);
    } finally {
      setLoadingProducts(false);
    }
  }, [fetchWithAuth]);

  const searchProducts = useCallback(async () => {
    setSearching(true);
    try {
      const res = await fetchWithAuth(`/api/admin/pos/search-product?q=${encodeURIComponent(searchQuery)}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.products || []);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  }, [fetchWithAuth, searchQuery]);

  useEffect(() => {
    loadLocations();
    loadAllProducts();
  }, [loadLocations, loadAllProducts]);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => searchProducts(), 300);
    } else {
      setSearchResults([]);
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery, searchProducts]);

  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.id);
    
    if (existing) {
      if (existing.quantity >= product.stock) {
        toast.error('Stock máximo alcanzado');
        return;
      }
      setCart(cart.map(item => 
        item.product_id === product.id 
          ? { ...item, quantity: item.quantity + 1, subtotal: (item.quantity + 1) * item.unit_price }
          : item
      ));
      toast.success('Cantidad actualizada');
    } else {
      if (product.stock < 1) {
        toast.error('Producto sin stock');
        return;
      }
      setCart([...cart, {
        product_id: product.id,
        product_code: product.product_code,
        product_name: product.inspired_by_name || product.name,
        product_brand: product.inspired_by_brand,
        product_image: product.main_image,
        gender: product.gender,
        quantity: 1,
        unit_price: product.price,
        subtotal: product.price,
        stock: product.stock
      }]);
      toast.success('Agregado al carrito');
    }
    
    setSearchQuery('');
    setSearchResults([]);
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        if (newQty < 1) return item;
        if (newQty > item.stock) {
          toast.error('Stock insuficiente');
          return item;
        }
        return { ...item, quantity: newQty, subtotal: newQty * item.unit_price };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const clearCart = () => {
    if (cart.length > 0 && window.confirm('¿Limpiar el carrito?')) {
      setCart([]);
      setShowCart(false);
    }
  };

  const calculateTotals = useCallback(() => {
    const subtotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
    const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    return { subtotal, discount: 0, total: subtotal, itemCount };
  }, [cart]);

  const processSale = useCallback(async (paymentMethod) => {
    if (!selectedLocation) {
      toast.error('Selecciona un punto de venta');
      return;
    }
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    setProcessing(true);
    const totals = calculateTotals();

    try {
      const res = await fetchWithAuth('/api/admin/pos/sales', {
        method: 'POST',
        body: {
          location_id: selectedLocation.id,
          items: cart.map(item => ({
            product_id: item.product_id,
            product_code: item.product_code,
            product_name: item.product_name,
            quantity: item.quantity,
            unit_price: item.unit_price,
            subtotal: item.subtotal
          })),
          payment_method: paymentMethod,
          subtotal: totals.subtotal,
          discount: totals.discount,
          total: totals.total,
          customer_name: customerName || null,
          notes: notes || null
        }
      });

      if (res.ok) {
        const data = await res.json();
        setLastSale(data.sale);
        setCart([]);
        setCustomerName('');
        setNotes('');
        setShowPaymentModal(false);
        setShowCart(false);
        toast.success(`¡Venta completada!`);
        loadAllProducts(); // Refresh stock
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al procesar venta');
      }
    } catch (error) {
      toast.error('Error al procesar venta');
    } finally {
      setProcessing(false);
    }
  }, [selectedLocation, cart, calculateTotals, fetchWithAuth, customerName, notes, loadAllProducts]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
  };

  const totals = calculateTotals();

  // Filter products by gender
  const filteredProducts = allProducts.filter(p => {
    if (activeGenderFilter === 'all') return true;
    // Unisex también aparece en Hombre y en Mujer, igual que en el catálogo
    if (activeGenderFilter === 'hombre') return p.gender === 'Hombre' || p.gender === 'Unisex';
    if (activeGenderFilter === 'mujer') return p.gender === 'Mujer' || p.gender === 'Unisex';
    if (activeGenderFilter === 'unisex') return p.gender === 'Unisex';
    return true;
  });

  const displayProducts = searchQuery.length >= 2 ? searchResults : filteredProducts;

  // Location Selection Screen
  if (!selectedLocation && locations.length > 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800/80 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full border border-gray-700/50 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-amber-500/30">
              <Store className="text-white" size={40} />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Alphatica POS</h1>
            <p className="text-gray-400">Selecciona tu punto de venta</p>
          </div>
          <div className="space-y-3">
            {locations.map(location => (
              <button
                key={location.id}
                onClick={() => setSelectedLocation(location)}
                className="w-full p-5 bg-gray-700/50 hover:bg-gray-700 rounded-2xl text-left transition-all duration-300 border border-gray-600/50 hover:border-amber-500/50 hover:shadow-lg hover:shadow-amber-500/10 group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-white font-semibold text-lg group-hover:text-amber-400 transition-colors">{location.name}</span>
                    {location.address && (
                      <span className="block text-sm text-gray-400 mt-1">{location.address}</span>
                    )}
                  </div>
                  <ChevronRight className="text-gray-500 group-hover:text-amber-400 transition-colors" size={24} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800/80 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full text-center border border-gray-700/50">
          <div className="w-20 h-20 bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Store className="text-gray-500" size={40} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Sin puntos de venta</h1>
          <p className="text-gray-400 mb-6">Configura un punto de venta para comenzar</p>
          <a href="/admin/pos/locations" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-semibold rounded-xl hover:shadow-lg hover:shadow-amber-500/30 transition-all">
            <Plus size={20} /> Crear Punto de Venta
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-testid="admin-pos">
      {/* Success Sale Modal */}
      {lastSale && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-green-900/90 to-gray-900/90 backdrop-blur-xl rounded-3xl p-8 max-w-sm w-full text-center border border-green-500/30 animate-in fade-in zoom-in duration-300">
            <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-green-500">
              <CheckCircle className="text-green-400" size={48} />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">¡Venta Exitosa!</h2>
            <p className="text-green-400 text-xl font-semibold mb-1">{lastSale.sale_number}</p>
            <p className="text-4xl font-bold text-white my-4">{formatCurrency(lastSale.total)}</p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800/50 rounded-full text-gray-300 mb-6">
              {lastSale.payment_method === 'cash' ? <Banknote size={18} /> : <CreditCard size={18} />}
              {lastSale.payment_method === 'cash' ? 'Efectivo' : 'Tarjeta'}
            </div>
            <button
              onClick={() => setLastSale(null)}
              className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-2xl hover:shadow-lg hover:shadow-green-500/30 transition-all"
            >
              Nueva Venta
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-gray-900/80 backdrop-blur-xl border-b border-gray-700/50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Logo & Location */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                <Sparkles className="text-white" size={20} />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-white leading-tight">Alphatica</h1>
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  <Store size={12} />
                  {selectedLocation?.name}
                </p>
              </div>
            </div>

            {/* Search Bar */}
            <div className="flex-1 max-w-xl relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar perfume..."
                className="w-full pl-11 pr-4 py-2.5 bg-gray-800/50 border border-gray-700 rounded-full text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all text-sm"
              />
              {searching && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-500 border-t-transparent"></div>
                </div>
              )}
            </div>

            {/* Cart Button */}
            <button
              onClick={() => setShowCart(true)}
              className="relative flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full text-black font-semibold hover:shadow-lg hover:shadow-amber-500/30 transition-all"
            >
              <ShoppingCart size={18} />
              <span className="hidden sm:inline">Carrito</span>
              {totals.itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {totals.itemCount}
                </span>
              )}
            </button>
          </div>

          {/* Gender Filter Pills */}
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={() => setActiveGenderFilter('all')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeGenderFilter === 'all' 
                  ? 'bg-white text-gray-900' 
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Todos
            </button>
            <button
              onClick={() => setActiveGenderFilter('hombre')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeGenderFilter === 'hombre' 
                  ? 'bg-cyan-500 text-black' 
                  : 'bg-gray-800 text-cyan-400 hover:bg-gray-700'
              }`}
            >
              Hombre
            </button>
            <button
              onClick={() => setActiveGenderFilter('mujer')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeGenderFilter === 'mujer' 
                  ? 'bg-pink-500 text-black' 
                  : 'bg-gray-800 text-pink-400 hover:bg-gray-700'
              }`}
            >
              Mujer
            </button>
            <button
              onClick={() => setActiveGenderFilter('unisex')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeGenderFilter === 'unisex' 
                  ? 'bg-white text-black' 
                  : 'bg-gray-800 text-white/80 hover:bg-gray-700'
              }`}
            >
              Unisex
            </button>
          </div>
        </div>
      </header>

      {/* Products Grid */}
      <main className="p-4 pb-24">
        {loadingProducts ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="bg-gray-800/50 rounded-2xl p-3 animate-pulse">
                <div className="aspect-square bg-gray-700 rounded-xl mb-3"></div>
                <div className="h-3 bg-gray-700 rounded mb-2"></div>
                <div className="h-4 bg-gray-700 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : displayProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Package className="text-gray-600 mb-4" size={64} />
            <p className="text-gray-400 text-lg">No se encontraron productos</p>
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="mt-4 text-amber-400 hover:text-amber-300"
              >
                Limpiar búsqueda
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {displayProducts.map(product => {
              const inCart = cart.find(item => item.product_id === product.id);
              const isOutOfStock = product.stock < 1;
              const genderColor = product.gender === 'Hombre' ? 'cyan' : 'pink';
              
              return (
                <button
                  key={product.id}
                  onClick={() => !isOutOfStock && addToCart(product)}
                  disabled={isOutOfStock}
                  className={`group relative bg-gray-800/50 backdrop-blur rounded-2xl p-3 text-left transition-all duration-300 border border-transparent hover:border-${genderColor}-500/50 hover:shadow-xl hover:shadow-${genderColor}-500/10 hover:scale-[1.02] ${
                    isOutOfStock ? 'opacity-50 cursor-not-allowed' : ''
                  } ${inCart ? `ring-2 ring-${genderColor}-500` : ''}`}
                >
                  {/* Product Image */}
                  <div className="relative aspect-square bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl overflow-hidden mb-3">
                    <img
                      src={product.main_image || 'https://via.placeholder.com/200'}
                      alt={product.inspired_by_name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    {/* Stock Badge */}
                    {isOutOfStock ? (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <span className="text-red-400 font-semibold text-sm">Agotado</span>
                      </div>
                    ) : (
                      <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 backdrop-blur rounded-full">
                        <span className="text-xs text-gray-300">{product.stock}</span>
                      </div>
                    )}
                    {/* In Cart Indicator */}
                    {inCart && (
                      <div className={`absolute top-2 left-2 w-6 h-6 bg-${genderColor}-500 rounded-full flex items-center justify-center`}>
                        <span className="text-xs font-bold text-black">{inCart.quantity}</span>
                      </div>
                    )}
                    {/* Gender indicator */}
                    <div className={`absolute bottom-2 left-2 w-2 h-2 rounded-full ${product.gender === 'Hombre' ? 'bg-cyan-400' : 'bg-pink-400'}`}></div>
                  </div>

                  {/* Product Info */}
                  <div className="space-y-1">
                    <p className="text-[10px] text-gray-500 font-medium tracking-wider">{product.product_code}</p>
                    <h3 className={`text-sm font-semibold leading-tight line-clamp-2 ${
                      product.gender === 'Hombre' ? 'text-cyan-300 group-hover:text-cyan-200' : 'text-pink-300 group-hover:text-pink-200'
                    }`}>
                      {product.inspired_by_name || product.name}
                    </h3>
                    <p className="text-xs text-gray-500 truncate">{product.inspired_by_brand}</p>
                    <p className="text-base font-bold text-white mt-2">
                      {formatCurrency(product.price)}
                    </p>
                  </div>

                  {/* Add to cart overlay */}
                  {!isOutOfStock && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl flex items-end justify-center pb-4">
                      <div className={`flex items-center gap-2 px-4 py-2 bg-${genderColor}-500 rounded-full text-black font-semibold text-sm`}>
                        <Plus size={16} />
                        Agregar
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </main>

      {/* Fixed Bottom Cart Summary (Mobile) */}
      {cart.length > 0 && !showCart && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-xl border-t border-gray-700/50 p-4 z-30 lg:hidden">
          <button
            onClick={() => setShowCart(true)}
            className="w-full flex items-center justify-between bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-4 text-black"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-black/20 rounded-full flex items-center justify-center">
                <ShoppingBag size={20} />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium opacity-80">{totals.itemCount} productos</p>
                <p className="text-lg font-bold">{formatCurrency(totals.total)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-semibold">
              Ver carrito
              <ChevronRight size={20} />
            </div>
          </button>
        </div>
      )}

      {/* Cart Drawer */}
      {showCart && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowCart(false)}
          />
          
          {/* Cart Panel */}
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-gray-900 shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
            {/* Cart Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
              <button
                onClick={() => setShowCart(false)}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft size={20} />
                <span className="text-sm">Seguir comprando</span>
              </button>
              {cart.length > 0 && (
                <button onClick={clearCart} className="text-sm text-red-400 hover:text-red-300">
                  Vaciar
                </button>
              )}
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mb-4">
                    <ShoppingBag className="text-gray-600" size={32} />
                  </div>
                  <p className="text-gray-400 text-lg mb-2">Tu carrito está vacío</p>
                  <p className="text-gray-500 text-sm">Agrega productos para comenzar</p>
                </div>
              ) : (
                cart.map(item => (
                  <div key={item.product_id} className="bg-gray-800/50 backdrop-blur rounded-2xl p-4 border border-gray-700/50">
                    <div className="flex gap-4">
                      <img
                        src={item.product_image || 'https://via.placeholder.com/80'}
                        alt=""
                        className="w-20 h-20 object-cover rounded-xl flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-[10px] text-gray-500 font-medium">{item.product_code}</p>
                            <h4 className={`font-semibold leading-tight ${
                              item.gender === 'Hombre' ? 'text-cyan-300' : 'text-pink-300'
                            }`}>
                              {item.product_name}
                            </h4>
                            <p className="text-xs text-gray-500">{item.product_brand}</p>
                          </div>
                          <button
                            onClick={() => removeFromCart(item.product_id)}
                            className="text-gray-500 hover:text-red-400 transition-colors p-1"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center bg-gray-700/50 rounded-full">
                            <button
                              onClick={() => updateQuantity(item.product_id, -1)}
                              className="p-2 hover:bg-gray-600 rounded-full transition-colors"
                              disabled={item.quantity <= 1}
                            >
                              <Minus size={14} className={item.quantity <= 1 ? 'text-gray-500' : 'text-white'} />
                            </button>
                            <span className="w-8 text-center text-white font-medium">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.product_id, 1)}
                              className="p-2 hover:bg-gray-600 rounded-full transition-colors"
                              disabled={item.quantity >= item.stock}
                            >
                              <Plus size={14} className={item.quantity >= item.stock ? 'text-gray-500' : 'text-white'} />
                            </button>
                          </div>
                          <p className="text-white font-bold">{formatCurrency(item.subtotal)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Cart Footer */}
            {cart.length > 0 && (
              <div className="border-t border-gray-700/50 p-4 space-y-4 bg-gray-900/80 backdrop-blur-xl">
                {/* Totals */}
                <div className="space-y-2">
                  <div className="flex justify-between text-gray-400">
                    <span>Subtotal ({totals.itemCount} productos)</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-2xl font-bold text-white">
                    <span>Total</span>
                    <span>{formatCurrency(totals.total)}</span>
                  </div>
                </div>

                {/* Checkout Button */}
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl text-black font-bold text-lg hover:shadow-lg hover:shadow-amber-500/30 transition-all flex items-center justify-center gap-2"
                >
                  <CreditCard size={20} />
                  Proceder al Pago
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-900 rounded-3xl w-full max-w-md overflow-hidden border border-gray-700/50 shadow-2xl animate-in zoom-in-95 duration-200 my-8 flex-shrink-0">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Finalizar Venta</h2>
              <button 
                onClick={() => setShowPaymentModal(false)} 
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Total Display */}
              <div className="text-center py-4 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700/50">
                <p className="text-gray-400 text-sm mb-1">Total a cobrar</p>
                <p className="text-5xl font-bold text-white">{formatCurrency(totals.total)}</p>
                <p className="text-gray-500 text-sm mt-2">{totals.itemCount} productos</p>
              </div>

              {/* Optional Fields */}
              <div className="space-y-3">
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Nombre del cliente (opcional)"
                    className="w-full bg-gray-800/50 border border-gray-700 rounded-xl py-3 pl-10 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all"
                  />
                </div>
                <div className="relative">
                  <StickyNote className="absolute left-3 top-3 text-gray-500" size={18} />
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notas (opcional)"
                    className="w-full bg-gray-800/50 border border-gray-700 rounded-xl py-3 pl-10 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all"
                  />
                </div>
              </div>

              {/* Payment Method Buttons */}
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => processSale('cash')}
                  disabled={processing}
                  className="relative overflow-hidden flex flex-col items-center gap-3 p-6 bg-gradient-to-br from-green-900/50 to-green-950/50 hover:from-green-800/50 hover:to-green-900/50 border-2 border-green-500/50 hover:border-green-400 rounded-2xl transition-all duration-300 disabled:opacity-50 group"
                >
                  <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Banknote className="text-green-400" size={28} />
                  </div>
                  <span className="text-green-400 font-semibold text-lg">Efectivo</span>
                </button>
                <button
                  onClick={() => processSale('card')}
                  disabled={processing}
                  className="relative overflow-hidden flex flex-col items-center gap-3 p-6 bg-gradient-to-br from-blue-900/50 to-blue-950/50 hover:from-blue-800/50 hover:to-blue-900/50 border-2 border-blue-500/50 hover:border-blue-400 rounded-2xl transition-all duration-300 disabled:opacity-50 group"
                >
                  <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <CreditCard className="text-blue-400" size={28} />
                  </div>
                  <span className="text-blue-400 font-semibold text-lg">Tarjeta</span>
                </button>
              </div>

              {/* Processing State */}
              {processing && (
                <div className="flex flex-col items-center py-4">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-amber-500/30 border-t-amber-500 mb-3"></div>
                  <p className="text-gray-400">Procesando venta...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPOS;
